import React from 'react';
import { CssBaseline, ThemeProvider, Container } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Routes, Route, useLocation, useNavigate, Link as RouterLink, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import theme from './theme';
import CustomButton from './widgets/button';
import Footer from './widgets/footer';

// Import des pages
import PresentationEtudiant from './pages/Presentation_etudiant';
import PresentationEntreprises from './pages/Presentation_entreprises';
import SolutionsEcoles from './pages/Solutions_ecoles';
import AbonnementsEcoles from './pages/Abonnements_ecoles';
import PresentationAssociations from './pages/Presentation_associations';
import APropos from './pages/A_propos';
import NousContacter from './pages/Nous_contacter';
import BientotDisponible from './pages/bientot_disponible';

// Style commun pour les éléments de navigation non sélectionnés
const NAV_ITEM_BASE_STYLE = {
    fontSize: '0.9rem', // Augmenté pour avoir une taille plus lisible et uniforme entre TabBar et MainAppBar
    fontWeight: 400,
    fontStyle: 'normal',
    textTransform: 'none'
};

function ResourcesDropdown() {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const location = useLocation();

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleMenuItemClick = (path) => {
        handleClose();
        navigate(path);
    };

    return (
        <>
            <Button
                id="resources-button"
                endIcon={<ArrowDropDownIcon />}
                onClick={handleClick}
                disableRipple
                sx={{
                    ...NAV_ITEM_BASE_STYLE,
                    color: 'black',
                    '&:hover,&:focus,&:active': { backgroundColor: 'transparent' }
                }}
            >
                Ressources
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                disableScrollLock
                MenuListProps={{ 'aria-labelledby': 'resources-button' }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: 2.5, // arrondi plus prononcé
                        mt: 1,
                        boxShadow: '0px 4px 18px rgba(0,0,0,0.18)',
                        border: '1px solid #e5e7eb'
                    },
                    '& .MuiMenuItem-root': {
                        borderRadius: 1,
                        mx: 0.5
                    }
                }}
            >
                <MenuItem
                    selected={location.pathname === '/contact'}
                    onClick={() => handleMenuItemClick('/contact')}
                    sx={{ fontSize: NAV_ITEM_BASE_STYLE.fontSize, fontWeight: NAV_ITEM_BASE_STYLE.fontWeight, textTransform: NAV_ITEM_BASE_STYLE.textTransform }}
                >
                    Nous contacter
                </MenuItem>
                <MenuItem
                    selected={location.pathname === '/a-propos'}
                    onClick={() => handleMenuItemClick('/a-propos')}
                    sx={{ fontSize: NAV_ITEM_BASE_STYLE.fontSize, fontWeight: NAV_ITEM_BASE_STYLE.fontWeight, textTransform: NAV_ITEM_BASE_STYLE.textTransform }}
                >
                    À propos
                </MenuItem>
            </Menu>
        </>
    );
}

function TabBar({ value = 0, onTabChange }) {
    const handleChange = (event, newValue) => onTabChange(newValue);
    return (
        <AppBar position="static" color="default" elevation={0} sx={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Tabs value={value} onChange={handleChange} indicatorColor="primary" textColor="inherit" sx={{ minHeight: 30 }}>
                    <Tab component={RouterLink} to="/etudiants" label="Étudiants" sx={{ ...NAV_ITEM_BASE_STYLE, minHeight: 30, color: 'black', opacity: 1, '&.Mui-selected': { fontWeight: 'bold', color: 'primary.main' } }} />
                    <Tab component={RouterLink} to="/entreprises" label="Entreprises" sx={{ ...NAV_ITEM_BASE_STYLE, minHeight: 30, color: 'black', opacity: 1, '&.Mui-selected': { fontWeight: 'bold', color: 'primary.main' } }} />
                    <Tab component={RouterLink} to="/ecoles" label="Écoles" sx={{ ...NAV_ITEM_BASE_STYLE, minHeight: 30, color: 'black', opacity: 1, '&.Mui-selected': { fontWeight: 'bold', color: 'primary.main' } }} />
                    <Tab component={RouterLink} to="/associations" label="Associations" sx={{ ...NAV_ITEM_BASE_STYLE, minHeight: 30, color: 'black', opacity: 1, '&.Mui-selected': { fontWeight: 'bold', color: 'primary.main' } }} />
                </Tabs>
            </Container>
        </AppBar>
    );
}

function MainAppBar({ selectedTab }) {
    const loginUrls = {
        0: '/bientot-disponible',
        1: 'https://plateforme-entreprise.studentlink.fr/',
        2: 'https://plateforme-ecole.studentlink.fr',
        3: 'https://plateforme-asso.studentlink.fr/#/main-route'
    };
    const loginHref = loginUrls[selectedTab] || loginUrls[0];
    const navigate = useNavigate();
    const location = useLocation();

    const getNavigationItems = () => {
        switch (selectedTab) {
            case 0:
                return [{ label: 'Présentation', to: '/etudiants' }];
            case 1:
                return [{ label: 'Présentation', to: '/entreprises' }];
            case 2:
                return [
                    { label: 'Solutions', to: '/ecoles' },
                    { label: 'Abonnements', to: '/ecoles/abonnements' }
                ];
            case 3:
                return [{ label: 'Présentation', to: '/associations' }];
            default:
                return [{ label: 'Présentation', to: '/etudiants' }];
        }
    };
    const navigationItems = getNavigationItems();

    return (
        <AppBar position="static" elevation={0} sx={{ background: 'white', borderBottom: '1px solid #e0e0e0' }}>
            <Toolbar disableGutters>
                <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src="/assets/images/logo.png" alt="Student Link Logo" style={{ height: 30, marginRight: 10, borderRadius: '20%' }} />
                        <Typography component="div" sx={{ color: 'black', fontFamily: '"Lilita One Local", "Lilita One", cursive', fontSize: '1.55rem', lineHeight: '30px', display: 'flex', alignItems: 'center' }}>
                            Student Link
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', ml: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {navigationItems.map((item) => {
                                const isActive = location.pathname === item.to;
                                return (
                                    <Button
                                        key={item.label}
                                        onClick={() => navigate(item.to)}
                                        disableRipple
                                        sx={{
                                            ...NAV_ITEM_BASE_STYLE,
                                            color: 'black',
                                            fontWeight: isActive ? 'bold' : NAV_ITEM_BASE_STYLE.fontWeight,
                                            '&:hover,&:focus,&:active': { backgroundColor: 'transparent' }
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                            <ResourcesDropdown />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {selectedTab === 0 ? (
                            <CustomButton component="a" href={loginHref} variant="contained" sx={{ color: 'white', px: 3, py: 1 }}>
                                Connexion
                            </CustomButton>
                        ) : (
                            <CustomButton component="a" href={loginHref} target="_blank" rel="noopener noreferrer" variant="contained" sx={{ color: 'white', px: 3, py: 1 }}>
                                Connexion
                            </CustomButton>
                        )}
                    </Box>
                </Container>
            </Toolbar>
        </AppBar>
    );
}

export default function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const [showTabBar, setShowTabBar] = React.useState(true);

    React.useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            setShowTabBar(currentScrollY <= 50);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top on route change
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [location.pathname]);

    // Gestion de l'onglet sélectionné : on ne le change que si l'URL correspond à une section principale
    const tabIndexFromPath = (path) => {
        if (path.startsWith('/entreprises')) return 1;
        if (path.startsWith('/ecoles')) return 2;
        if (path.startsWith('/associations')) return 3;
        if (path.startsWith('/etudiants')) return 0;
        return null; // pages "Ressources" : on ne force pas de tab
    };

    const initialTab = React.useMemo(() => {
        const byPath = tabIndexFromPath(location.pathname);
        if (byPath !== null) return byPath;
        const stored = localStorage.getItem('sl_selected_tab');
        return stored !== null ? parseInt(stored, 10) : 0;
    }, []); // calculé une seule fois

    const [selectedTab, setSelectedTab] = React.useState(initialTab);

    // Met à jour l'état quand on change vers une route principale
    React.useEffect(() => {
        const idx = tabIndexFromPath(location.pathname);
        if (idx !== null && idx !== selectedTab) {
            setSelectedTab(idx);
            localStorage.setItem('sl_selected_tab', idx.toString());
        }
        // Si idx === null (page ressources), on ne change rien -> l'onglet reste celui précédemment choisi
    }, [location.pathname, selectedTab]);

    const handleTabChange = (newTab) => {
        setSelectedTab(newTab);
        localStorage.setItem('sl_selected_tab', newTab.toString());
        switch (newTab) {
            case 0:
                navigate('/etudiants');
                break;
            case 1:
                navigate('/entreprises');
                break;
            case 2:
                navigate('/ecoles');
                break;
            case 3:
                navigate('/associations');
                break;
            default:
                navigate('/etudiants');
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Helmet>
                <title>Student Link — Career Center IA</title>
                <link rel="canonical" href="https://studentlink.fr" />
            </Helmet>
            <CssBaseline />
            <Routes>
                {/* Route standalone sans navigation */}
                <Route path="/bientot-disponible" element={<BientotDisponible />} />

                {/* Routes avec navigation complète */}
                <Route path="*" element={
                    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, transform: showTabBar ? 'translateY(0)' : 'translateY(-100%)', transition: 'transform 0.15s ease-out' }}>
                            <TabBar value={selectedTab} onTabChange={handleTabChange} />
                        </Box>
                        <Box sx={{ position: 'fixed', top: showTabBar ? '40px' : '0px', left: 0, right: 0, zIndex: 1100, transition: 'top 0.15s ease-out' }}>
                            <MainAppBar selectedTab={selectedTab} />
                        </Box>
                        <Box sx={{ flexGrow: 1, marginTop: showTabBar ? '110px' : '64px', transition: 'margin-top 0.15s ease-out' }}>
                            <Routes>
                                <Route path="/" element={<Navigate to="/etudiants" replace />} />
                                <Route path="/etudiants" element={<PresentationEtudiant />} />
                                <Route path="/entreprises" element={<PresentationEntreprises />} />
                                <Route path="/ecoles" element={<SolutionsEcoles />} />
                                <Route path="/ecoles/abonnements" element={<AbonnementsEcoles />} />
                                <Route path="/associations" element={<PresentationAssociations />} />
                                <Route path="/a-propos" element={<APropos />} />
                                <Route path="/contact" element={<NousContacter />} />
                            </Routes>
                        </Box>
                        <Footer />
                    </Box>
                } />
            </Routes>
        </ThemeProvider>
    );
}
